import { Link, Outlet, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useDeferredValue, useEffect, useMemo, useState, type FormEvent } from 'react';
import type { Audience, CollectionSummary, RealmCounts } from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { realmFile } from '../db/protocol.ts';
import { formatDate } from '../files.ts';
import { AUDIENCE_NAMES, REALM_LIMITS, exportJson, slugify } from '@realmery/realm-core';
import { htmlExport } from '../export/html.ts';
import { markdownFiles } from '../export/markdown.ts';
import { renderFoggedJpegs, renderMapJpegs } from '../export/mapPicture.ts';
import { spellingChanged, useSpellingVersion } from '../spelling/client.ts';
import { SITE_AUDIENCES, staticSiteFiles, type SiteAudience } from '../export/staticSite.ts';
import { zip } from '../export/zip.ts';
import { saveBytes } from '../files.ts';
import { AudienceField, audienceName } from './AudienceField.tsx';
import { PlayerViewHost } from '../canvas/PlayerViewHost.tsx';
import type { ModeSlug } from './KnowledgeModesPage.tsx';
import { claimStart, rememberLastRealm, settings } from '../settings.ts';
import { keyLabel, keysOf, useShortcut, useShortcutKeys } from '../shortcuts.ts';
import { COLLECTION_NAME_MAX } from './EntryCollections.tsx';
import { PreviewBanner, PreviewButtons } from './PreviewCopy.tsx';
import { LeaseKeeper } from './LeaseKeeper.tsx';

const route = getRouteApi('/_app/realm/$realmKey');

const SIDEBAR_KEY = 'realmery.sidebar.minimized';

/** The tools that read a realm shared with this account; the others are the owner's own, left out of its copy. */
const READ_TOOLS: ReadonlySet<string> = new Set(['/realm/$realmKey/graph', '/realm/$realmKey/chronology', '/realm/$realmKey/atlas', '/realm/$realmKey/modes', '/realm/$realmKey/calendars']);

/** The realm's tools, in the sidebar by name and in its minimized rail by icon. */
const TOOLS = [
  { to: '/realm/$realmKey/graph', label: 'Knowledge graph', icon: '🕸️', title: "The realm's entries and relationships as a graph, family trees and hierarchies" },
  { to: '/realm/$realmKey/chronology', label: 'Chronology', icon: '⏳', title: "Timelines of the realm's eras and events" },
  { to: '/realm/$realmKey/story', label: 'Story Studio', icon: '📖', title: 'Story Studio: novels and stories, in acts, chapters and scenes' },
  { to: '/realm/$realmKey/consistency', label: 'Consistency Check', icon: '⚖️', title: "Contradictions and loose ends in the realm's facts, relationships and dates" },
  { to: '/realm/$realmKey/campaigns', label: 'Game Master Toolkit', icon: '🛡️', title: 'The Game Master Toolkit: campaigns, sessions, encounters and quests' },
  { to: '/realm/$realmKey/atlas', label: 'Atlas', icon: '🗺️', title: "The Atlas: the realm's maps, with their pins and regions" },
  { to: '/realm/$realmKey/boards', label: 'Idea Board', icon: '📌', title: 'The Idea Board: notes, cards, shapes and pictures, joined with arrows' },
  { to: '/realm/$realmKey/modes', label: 'Knowledge Modes', icon: '🧭', title: 'Knowledge Modes: the realm walked column by column, for exploring, writing, running a game, design and research' },
  { to: '/realm/$realmKey/calendars', label: 'Calendar', icon: '📅', title: "The realm's own calendars, month by month" },
  { to: '/realm/$realmKey/tables', label: 'Random tables', icon: '🎲', title: 'Random tables: roll for encounters, weather, loot and more' },
  { to: '/realm/$realmKey/sheets', label: 'Character sheets', icon: '📜', title: "Character sheets for the realm's people and creatures" },
  { to: '/realm/$realmKey/generators', label: 'Generators and scripts', icon: '⚙️', title: 'Quick generators and scripts that fill the realm' },
] as const;

export function RealmLayout() {
  const { realm, entries, collections, shared, readOnly } = route.useLoaderData();
  const { realmKey } = route.useParams();
  // A Player's or Reader's copy leaves out the owner's own tools; a co-owner has them all.
  const copyOnly = shared !== null && shared.role !== 'editor';
  const tools = copyOnly ? TOOLS.filter((t) => READ_TOOLS.has(t.to)) : TOOLS;
  // For "Reopen the last realm"; opening a realm also counts as this page load's start.
  useEffect(() => {
    claimStart();
    rememberLastRealm(realmKey);
  }, [realmKey]);
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [collectionId, setCollectionId] = useState('');
  /** The sidebar folded to a slim rail, for more room; remembered in this browser. */
  const [minimized, setMinimizedState] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });
  const setMinimized = (on: boolean) => {
    setMinimizedState(on);
    try {
      localStorage.setItem(SIDEBAR_KEY, on ? '1' : '0');
    } catch {
      // Private browsing: it's only for this visit.
    }
  };
  const deferredQuery = useDeferredValue(query);
  const navigate = useNavigate();
  // Knowledge Modes' keys, from anywhere in the realm (the desktop's mode.* actions).
  const toMode = (mode: ModeSlug) => void navigate({ to: '/realm/$realmKey/modes', params: { realmKey }, search: { mode } });
  useShortcut('mode.explorer', () => toMode('explorer'));
  useShortcut('mode.writer', () => toMode('writer'));
  useShortcut('mode.gameMaster', () => toMode('game-master'));
  useShortcut('mode.architect', () => toMode('architect'));
  useShortcut('mode.research', () => toMode('research'));

  // app.findReplace (Ctrl+Shift+F) opens Search from anywhere in the realm, as the desktop's Find and Replace.
  useShortcut('app.findReplace', () => {
    const q = query.trim();
    void navigate({ to: '/realm/$realmKey/search', params: { realmKey }, search: q ? { q } : {} });
  });
  useShortcutKeys();
  const searchKeys = keysOf('app.findReplace').map(keyLabel).join(' or ');

  const types = useMemo(() => [...new Set(entries.map((e) => e.typeName))].sort(), [entries]);
  const visible = useMemo(() => {
    const needle = deferredQuery.trim().toLowerCase();
    const members = collectionId ? new Set(collections.find((c) => c.id === collectionId)?.entryIds) : null;
    return entries.filter(
      (entry) =>
        (!type || entry.typeName === type) &&
        (!members || members.has(entry.id)) &&
        (!needle ||
          entry.title.toLowerCase().includes(needle) ||
          entry.aliases.some((a) => a.toLowerCase().includes(needle)) ||
          entry.labels.some((l) => l.includes(needle))),
    );
  }, [entries, collections, deferredQuery, type, collectionId]);

  return (
    <div className={minimized ? 'realm-layout sidebar-minimized' : 'realm-layout'}>
      {minimized ? (
        <aside className="sidebar minimized" aria-label="Entries">
          <button type="button" className="quiet sidebar-toggle" title="Show the sidebar: entries, search and the realm's tools" aria-label="Show the sidebar" onClick={() => setMinimized(false)}>
            »
          </button>
          <nav className="sidebar-rail" aria-label="The realm's tools">
            <Link to="/realm/$realmKey" params={{ realmKey }} className="rail-link" title={`${realm.name}: the realm's overview`} aria-label="The realm's overview" activeOptions={{ exact: true }}>
              <span aria-hidden="true">🏰</span>
            </Link>
            {!readOnly && (
              <Link to="/realm/$realmKey/new" params={{ realmKey }} className="rail-link" title="New entry" aria-label="New entry">
                <span aria-hidden="true">📝</span>
              </Link>
            )}
            <Link to="/realm/$realmKey/search" params={{ realmKey }} search={{}} className="rail-link" title={`Search all text${searchKeys ? ` (${searchKeys})` : ''}`} aria-label="Search all text">
              <span aria-hidden="true">🔍</span>
            </Link>
            {tools.map((tool) => (
              <Link key={tool.to} to={tool.to} params={{ realmKey }} search={{}} className="rail-link" title={`${tool.label}: ${tool.title}`} aria-label={tool.label}>
                <span aria-hidden="true">{tool.icon}</span>
              </Link>
            ))}
          </nav>
        </aside>
      ) : (
        <aside className="sidebar" aria-label="Entries">
          <div className="sidebar-head">
            <div className="sidebar-head-row">
              <Link to="/" className="back-link">
                ← All realms
              </Link>
              <button type="button" className="quiet sidebar-toggle" title="Minimize the sidebar, for more room" aria-label="Minimize the sidebar" onClick={() => setMinimized(true)}>
                «
              </button>
            </div>
            <h1 className="sidebar-title">
              <Link to="/realm/$realmKey" params={{ realmKey }}>
                {realm.name}
              </Link>
            </h1>
            {!readOnly && (
              <Link to="/realm/$realmKey/new" params={{ realmKey }} className="button primary new-entry-link">
                New entry
              </Link>
            )}
            <input
              type="search"
              placeholder="Search titles, aliases, labels"
              aria-label="Search entries"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
            />
            <div className="filters">
              <select aria-label="Filter by type" value={type} onChange={(event) => setType(event.currentTarget.value)}>
                <option value="">All types</option>
                {types.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
              <select aria-label="Filter by collection" value={collectionId} onChange={(event) => setCollectionId(event.currentTarget.value)}>
                <option value="">All collections</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            {!readOnly && (
              <CollectionTools
                file={realmFile(realmKey)}
                chosen={collections.find((c) => c.id === collectionId) ?? null}
                onCreated={setCollectionId}
                onDeleted={() => setCollectionId('')}
              />
            )}
          </div>
          <nav className="entry-list">
            <Link
              to="/realm/$realmKey/search"
              params={{ realmKey }}
              search={query.trim() ? { q: query.trim() } : {}}
              className="search-text-link"
              title={`Search every entry's text, titles, aliases, labels and fields${searchKeys ? ` (${searchKeys})` : ''}`}
            >
              {query.trim() ? `Search the text for “${query.trim()}”` : 'Search all text…'}
            </Link>
            {tools.map((tool) => (
              <Link key={tool.to} to={tool.to} params={{ realmKey }} search={{}} className="search-text-link" title={tool.title}>
                {tool.label}
              </Link>
            ))}
            {visible.length === 0 && <p className="meta">No entries match.</p>}
            {visible.map((entry) => (
              <Link
                key={entry.id}
                to="/realm/$realmKey/entry/$entryId"
                params={{ realmKey, entryId: entry.id }}
                className="entry-link"
                activeProps={{ className: 'entry-link active', 'aria-current': 'page' }}
              >
                <span className="entry-link-title">
                  {entry.locked && <span aria-label="Locked" title="Locked">🔒 </span>}
                  {entry.title}
                </span>
                <span className="entry-link-type">{entry.typeName}</span>
              </Link>
            ))}
          </nav>
        </aside>
      )}
      <section className="realm-main">
        {shared?.previewOf && <PreviewBanner shared={shared} />}
        {shared?.role === 'editor' && (
          <p className="shared-banner" role="note">
            Shared with you by {shared.ownerEmail}, as a Co-owner: you can change everything, taking turns with them. Only they can share it or
            delete it.
          </p>
        )}
        <LeaseKeeper />
        {shared && !shared.previewOf && shared.role !== 'editor' && (
          <p className="shared-banner" role="note">
            Shared with you by {shared.ownerEmail}, as a {shared.role === 'player' ? 'Player' : 'Reader'}: you can read it, not change it.
            It&apos;s their copy for you, updated when they choose.
          </p>
        )}
        <PlayerViewHost file={realmFile(realmKey)}>
          <Outlet />
        </PlayerViewHost>
      </section>
    </div>
  );
}

/**
 * The desktop's + and Audience… beside the collection filter (docs/ENTRY-EDITOR-SLICE-3-PLAN.md
 * section 3.3): make a collection, or set who the chosen one is for. Renaming and deleting
 * one are the web's first (docs/PARITY-STEP-1-PLAN.md section 3).
 */
function CollectionTools({ file, chosen, onCreated, onDeleted }: {
  file: string;
  chosen: CollectionSummary | null;
  onCreated: (id: string) => void;
  onDeleted: () => void;
}) {
  const router = useRouter();
  const [panel, setPanel] = useState<'none' | 'new' | 'audience' | 'rename' | 'delete'>('none');
  const [name, setName] = useState('');
  const [audience, setAudience] = useState<Audience>({ role: 'READER', customLabel: '' });
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  async function run(work: () => Promise<void>, fallback: string) {
    setWorking(true);
    setError('');
    try {
      await work();
      await router.invalidate();
      setPanel('none');
    } catch (e) {
      setError(e instanceof RealmClientError ? e.message : fallback);
    } finally {
      setWorking(false);
    }
  }

  function create(event: FormEvent) {
    event.preventDefault();
    if (name.trim() === '') return;
    void run(async () => {
      const { id } = await realms.createCollection(file, name);
      setName('');
      onCreated(id);
    }, 'The collection could not be made.');
  }

  function rename(event: FormEvent) {
    event.preventDefault();
    if (!chosen || name.trim() === '') return;
    void run(async () => {
      await realms.renameCollection(file, chosen.id, name);
      setName('');
    }, 'The collection could not be renamed.');
  }

  function remove() {
    if (!chosen) return;
    void run(async () => {
      await realms.deleteCollection(file, chosen.id);
      onDeleted();
    }, 'The collection could not be deleted.');
  }

  function saveAudience(event: FormEvent) {
    event.preventDefault();
    if (!chosen) return;
    void run(() => realms.setCollectionAudience(file, chosen.id, audience), "The collection's audience could not be saved.");
  }

  return (
    <div className="collection-tools">
      {panel === 'none' && (
        <div className="actions">
          <button type="button" className="quiet" onClick={() => setPanel('new')}>
            + New collection
          </button>
          {chosen && (
            <button
              type="button"
              className="quiet"
              title={`Who “${chosen.name}” is for`}
              onClick={() => {
                setAudience(chosen.audience);
                setPanel('audience');
              }}
            >
              Audience: {audienceName(chosen.audience)}…
            </button>
          )}
          {chosen && (
            <button
              type="button"
              className="quiet"
              onClick={() => {
                setName(chosen.name);
                setPanel('rename');
              }}
            >
              Rename…
            </button>
          )}
          {chosen && (
            <button type="button" className="quiet" onClick={() => (settings()['general.confirmDeletes'] ? setPanel('delete') : remove())}>
              Delete…
            </button>
          )}
        </div>
      )}
      {panel === 'rename' && chosen && (
        <form onSubmit={rename} aria-label={`Rename ${chosen.name}`}>
          <label>
            New name
            <input value={name} maxLength={COLLECTION_NAME_MAX} autoFocus onChange={(event) => setName(event.currentTarget.value)} />
          </label>
          <div className="actions">
            <button type="submit" className="primary" disabled={working || name.trim() === ''}>
              Rename
            </button>
            <button
              type="button"
              onClick={() => {
                setName('');
                setPanel('none');
              }}
              disabled={working}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
      {panel === 'delete' && chosen && (
        <div role="alertdialog" aria-label={`Delete ${chosen.name}`} className="collection-delete">
          <p>
            Delete “{chosen.name}”? Its {chosen.entryIds.length === 1 ? 'entry stays' : `${chosen.entryIds.length} entries stay`} in the realm; only
            the collection goes.
          </p>
          <div className="actions">
            <button type="button" className="danger" disabled={working} onClick={remove}>
              Delete collection
            </button>
            <button type="button" onClick={() => setPanel('none')} disabled={working}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {panel === 'new' && (
        <form onSubmit={create} aria-label="New collection">
          <label>
            Collection name
            <input value={name} maxLength={COLLECTION_NAME_MAX} autoFocus onChange={(event) => setName(event.currentTarget.value)} />
          </label>
          <div className="actions">
            <button type="submit" className="primary" disabled={working || name.trim() === ''}>
              Make collection
            </button>
            <button type="button" onClick={() => setPanel('none')} disabled={working}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {panel === 'audience' && chosen && (
        <form onSubmit={saveAudience} aria-label={`Audience of ${chosen.name}`}>
          <AudienceField label={`Who “${chosen.name}” is for`} value={audience} onChange={(next) => setAudience(next)} />
          <div className="actions">
            <button type="submit" className="primary" disabled={working}>
              Save
            </button>
            <button type="button" onClick={() => setPanel('none')} disabled={working}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

type ExportFormat = 'json' | 'markdown' | 'html' | 'site';

/**
 * The desktop's File > Export Realm (docs/PARITY-STEP-1-PLAN.md section 4): the worker
 * gathers the realm, and the page builds the file and saves it, named as the desktop
 * names it. The .realmery file itself is exported from the realms page.
 */
function RealmExports({ file, name, realmKey }: { file: string; name: string; realmKey: string }) {
  const [working, setWorking] = useState<ExportFormat | null>(null);
  const [error, setError] = useState('');
  /** The static website asks who it's for first (`askAudienceThreshold`). */
  const [choosingSite, setChoosingSite] = useState(false);
  const [siteAudience, setSiteAudience] = useState<SiteAudience>('READER');

  async function save(format: ExportFormat) {
    setWorking(format);
    setError('');
    try {
      const data = await realms.exportData(file);
      const base = slugify(name);
      const encoder = new TextEncoder();
      if (format === 'json') saveBytes(encoder.encode(exportJson(data)), `${base}.json`, 'application/json');
      else if (format === 'html') saveBytes(encoder.encode(htmlExport(data)), `${base}.html`, 'text/html');
      else if (format === 'site') {
        const files = staticSiteFiles(data, siteAudience, await renderFoggedJpegs(data.atlas, siteAudience === 'OWNER'));
        saveBytes(zip(files), `${base}-website-${siteAudience.toLowerCase()}.zip`, 'application/zip');
        setChoosingSite(false);
      }
      else saveBytes(zip(markdownFiles(data, await renderMapJpegs(data.atlas, true))), `${base}-markdown.zip`, 'application/zip');
    } catch (e) {
      setError(e instanceof RealmClientError ? e.message : 'The export could not be made.');
    } finally {
      setWorking(null);
    }
  }

  return (
    <section className="realm-exports" aria-label="Export">
      <h2>Export</h2>
      <div className="actions">
        <button type="button" disabled={working !== null} onClick={() => void save('json')}>
          {working === 'json' ? 'Exporting…' : 'JSON'}
        </button>
        <button type="button" disabled={working !== null} onClick={() => void save('markdown')}>
          {working === 'markdown' ? 'Exporting…' : 'Markdown (.zip)'}
        </button>
        <button type="button" disabled={working !== null} onClick={() => void save('html')}>
          {working === 'html' ? 'Exporting…' : 'HTML'}
        </button>
        <button type="button" disabled={working !== null} aria-expanded={choosingSite} onClick={() => setChoosingSite(!choosingSite)}>
          Static website (.zip)…
        </button>
        <Link to="/realm/$realmKey/print" params={{ realmKey }} className="button" title="The realm laid out for printing, or Save as PDF">
          PDF…
        </Link>
      </div>
      {choosingSite && (
        <form
          className="panel"
          aria-label="Static website"
          onSubmit={(e) => {
            e.preventDefault();
            void save('site');
          }}
        >
          <p>A website of the realm, a page for each entry and map, to host or hand to your players.</p>
          <label>
            Include what's visible to
            <select value={siteAudience} onChange={(e) => setSiteAudience(e.currentTarget.value as SiteAudience)}>
              {[...SITE_AUDIENCES].reverse().map((a) => (
                <option key={a} value={a}>
                  {AUDIENCE_NAMES[a]} or more public
                </option>
              ))}
            </select>
          </label>
          <p className="meta">
            {siteAudience === 'OWNER'
              ? 'Everything: GM-only places, every drawing layer, the whole of each map, and the stories.'
              : 'A custom audience (such as "GM eyes only") is left out. Maps show what players see: GM-only places and layers left out, and the fog of war painted in.'}
          </p>
          <div className="actions">
            <button type="submit" className="primary" disabled={working !== null}>
              {working === 'site' ? 'Exporting…' : 'Export website'}
            </button>
            <button type="button" onClick={() => setChoosingSite(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      <p className="meta">
        The entries, collections, maps and stories, as Realmery on the desktop exports them.
      </p>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/** The realm's name and description, with Edit details to change them, and Sharing. */
function RealmDetails({
  file,
  realmKey,
  name,
  description,
  readOnly,
  own,
  coOwned,
}: {
  file: string;
  realmKey: string;
  name: string;
  description: string;
  readOnly: boolean;
  own: boolean;
  coOwned: boolean;
}) {
  const router = useRouter();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name, description });
  const [error, setError] = useState('');
  if (!editing || readOnly) {
    return (
      <>
        <h1>{name}</h1>
        {description && <p className="lead realm-description pre-line">{description}</p>}
        {!readOnly && <div className="actions">
          <button
            type="button"
            className="quiet"
            title="Rename the realm, or change its description"
            onClick={() => {
              setDraft({ name, description });
              setError('');
              setEditing(true);
            }}
          >
            Edit details…
          </button>
          {(own || coOwned) && (
            <Link
              className="button quiet"
              to="/realm/$realmKey/sharing"
              params={{ realmKey }}
              title={own ? 'Share the realm with co-owners, players and readers' : 'Who the realm is shared with'}
            >
              Sharing…
            </Link>
          )}
          {own && <PreviewButtons file={file} />}
        </div>}
      </>
    );
  }
  return (
    <form
      className="panel"
      aria-label="Realm details"
      onSubmit={(e) => {
        e.preventDefault();
        setError('');
        void realms
          .updateRealmDetails(file, draft.name, draft.description)
          .then(() => router.invalidate())
          .then(
            () => setEditing(false),
            (err: unknown) => setError(err instanceof RealmClientError ? err.message : 'The details could not be saved.'),
          );
      }}
    >
      <label>
        Name
        <input value={draft.name} required maxLength={REALM_LIMITS.name} autoFocus onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })} />
      </label>
      <label>
        Description
        <textarea rows={5} value={draft.description} maxLength={REALM_LIMITS.description} onChange={(e) => setDraft({ ...draft, description: e.currentTarget.value })} />
      </label>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      <div className="actions">
        <button type="submit" className="primary">
          Save
        </button>
        <button type="button" onClick={() => setEditing(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * The realm's own dictionary (docs/PARITY-STEP-6-PLAN.md section 6): words its world uses
 * that no dictionary has, added from a misspelling's menu, and kept in the realm so they
 * travel with it, to the desktop too.
 */
function RealmDictionary({ file }: { file: string }) {
  const version = useSpellingVersion();
  const [words, setWords] = useState<readonly string[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    void realms.proofingContext(file, null).then((c) => live && setWords(c.dictionary));
    return () => {
      live = false;
    };
  }, [file, version]);
  if (words === null) return null;
  return (
    <section className="realm-dictionary" aria-label="Realm Dictionary">
      <h2>Realm Dictionary</h2>
      {words.length === 0 ? (
        <p className="meta">No words yet. Right-click a misspelled name or word from your world and choose Add to Realm Dictionary. Entry titles and aliases are always accepted.</p>
      ) : (
        <ul className="dictionary-words">
          {words.map((word) => (
            <li key={word}>
              <span>{word}</span>
              <button
                type="button"
                className="quiet"
                aria-label={`Remove ${word}`}
                onClick={() =>
                  void realms.removeFromRealmDictionary(file, word).then(spellingChanged, (e: unknown) => setError(e instanceof RealmClientError ? e.message : 'The word could not be removed.'))
                }
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/** The desktop's File > Realm Audience…: who the whole realm is for, kept in its settings. */
function RealmAudience({ file, name, audience }: { file: string; name: string; audience: Audience }) {
  const router = useRouter();
  const [editing, setEditing] = useState(false);
  const [chosen, setChosen] = useState<Audience>(audience);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setWorking(true);
    setError('');
    try {
      await realms.setRealmAudience(file, chosen);
      await router.invalidate();
      setEditing(false);
    } catch (e) {
      setError(e instanceof RealmClientError ? e.message : "The realm's audience could not be saved.");
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="realm-audience">
      {editing ? (
        <form onSubmit={save} aria-label={`Audience of ${name}`}>
          <AudienceField label={`Who can see “${name}”?`} value={chosen} onChange={(next) => setChosen(next)} />
          <div className="actions">
            <button type="submit" className="primary" disabled={working}>
              Save
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={working}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <p>
          For: <span className="chip" title="Who this realm is for">{audienceName(audience)}</span>{' '}
          <button
            type="button"
            className="quiet"
            onClick={() => {
              setChosen(audience);
              setEditing(true);
            }}
          >
            Change…
          </button>
        </p>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const COUNT_LABELS: readonly [keyof RealmCounts, string, string][] = [
  ['entries', 'entry', 'entries'],
  ['collections', 'collection', 'collections'],
  ['maps', 'map', 'maps'],
  ['timelines', 'timeline', 'timelines'],
  ['calendars', 'calendar', 'calendars'],
  ['boards', 'idea board', 'idea boards'],
  ['stories', 'story', 'stories'],
  ['campaigns', 'campaign', 'campaigns'],
  ['sheets', 'character sheet', 'character sheets'],
  ['randomTables', 'random table', 'random tables'],
  ['scripts', 'script', 'scripts'],
];

/** What the web shows besides entries and collections; the rest opens on the desktop. */
const ON_THE_WEB: ReadonlySet<keyof RealmCounts> = new Set(['maps', 'timelines', 'calendars', 'boards', 'stories', 'campaigns', 'sheets', 'randomTables', 'scripts']);

export function RealmOverview() {
  const { realm, counts, audience, shared, readOnly } = route.useLoaderData();
  const { realmKey } = route.useParams();
  const others = COUNT_LABELS.filter(([key]) => key !== 'entries' && key !== 'collections' && counts[key] > 0);
  const desktopOnly = others.filter(([key]) => !ON_THE_WEB.has(key));
  return (
    <article className="document">
      <p className="eyebrow">Realm</p>
      <RealmDetails
        file={realmFile(realmKey)}
        realmKey={realmKey}
        name={realm.name}
        description={realm.description}
        readOnly={readOnly}
        own={shared === null}
        coOwned={shared?.role === 'editor'}
      />
      <dl className="stats">
        {COUNT_LABELS.filter(([key]) => counts[key] > 0 || key === 'entries').map(([key, one, many]) => (
          <div key={key}>
            <dt>{counts[key] === 1 ? one : many}</dt>
            <dd>{counts[key]}</dd>
          </div>
        ))}
      </dl>
      {!readOnly && <RealmAudience file={realmFile(realmKey)} name={realm.name} audience={audience} />}
      <RealmExports file={realmFile(realmKey)} name={realm.name} realmKey={realmKey} />
      {!readOnly && <RealmDictionary file={realmFile(realmKey)} />}
      <p className="meta">
        Created {formatDate(realm.created)} · edited {formatDate(realm.modified)} · realm format {realm.schemaVersion}
      </p>
      {desktopOnly.length > 0 && (
        <p className="callout">
          The realm's {desktopOnly.map(([, , many]) => many).join(', ')} are kept safe in it and open in Realmery on the
          desktop.
        </p>
      )}
    </article>
  );
}
